import React from 'react';
import noop from 'lodash/noop';
import { Box, FadeIn, Flex, Spinner } from 'pounceV2';
import { useTheme } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { AIInferenceStreamUsage, AIToolInvocation, RiskScoreEvidence } from 'Generated/schema';
import EnrichedAIResponseV3 from 'Components/AI/EnrichedAIResponseV3';
import { PromptMarkdownPreviewV3 } from 'Components/fields/TextArea/promptMarkdown';
import PromptActionsV3 from 'Components/AIExplainLogEvents/PromptActionsV3';
import { ToolInvocationProvider } from 'Components/AIExplainLogEvents/ToolInvocationContext';
import CollapsibleTextV3 from 'Components/CollapsibleText/CollapsibleTextV3';
import { useAutoCollapsePrompts } from 'Hooks/useAutoCollapsePrompts';
import {
  MESSAGE_ROLE,
  MESSAGE_ROLE_ATTR,
  PRINT_SUPPRESS_ATTR,
} from 'Components/utils/printAIResponse';
import { useAutoScroll } from 'Pages/AI/hooks/useAutoScroll';
import useInfiniteScroll from 'Hooks/useInfiniteScroll';
import RecommendedActionsV3 from './RecommendedActionsV3';
import {
  extractPantherPrompts,
  stripRecommendedSection,
  RecommendedAction,
} from '../enrichedAIResponseUtils';

export interface ConversationMessage {
  streamId: string;
  userPrompt: string;
  aiResponse: string;
  usage?: AIInferenceStreamUsage | null;
  riskScoreEvidence?: RiskScoreEvidence | null;
  toolInvocations?: AIToolInvocation[];
  finished: boolean;
  createdAt?: string;
}

interface MessageListProps {
  messages: ConversationMessage[];
  onSearchRequest?: (query: string) => void;
  onRiskExplain?: () => void;
  onPromptClick?: (prompt: string) => void;
  forceScrollToBottom?: number; // Increment this to trigger instant scroll
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  hasMore?: boolean;
  loadMore?: () => void;
  isLoadingMore?: boolean;
  isCancelled?: boolean;
  isThinking?: boolean;
  /** Optional row rendered as the first item inside the Next Steps card (before AI prompts). */
  recommendedActionsHeader?: React.ReactNode;
  /** Optional row rendered below the Next Steps card (outside it). */
  recommendedActionsRow?: React.ReactNode;
}

/**
 * Renders a timeline of conversation messages.
 * Displays completed messages from history and the currently streaming message.
 *
 * Users can scroll up freely; auto-scroll only engages when near the bottom.
 */
const MessageListV3: React.FC<MessageListProps> = ({
  messages,
  onSearchRequest,
  onRiskExplain,
  onPromptClick,
  forceScrollToBottom,
  scrollContainerRef,
  hasMore,
  loadMore,
  isLoadingMore = false,
  isCancelled = false,
  isThinking = false,
  recommendedActionsHeader,
  recommendedActionsRow,
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  const [autoCollapsePrompts] = useAutoCollapsePrompts();
  // Per-message manual expand/collapse choices — take precedence over autoCollapsePrompts
  // and are cleared when a new message arrives so old prompts re-collapse automatically.
  const [promptOverrides, setPromptOverrides] = React.useState<Map<string, boolean>>(
    () => new Map(),
  );

  const hasActiveStream = messages.some(msg => !msg.finished);
  const newestStreamId = messages[messages.length - 1]?.streamId ?? null;

  // Fire only on a true append: list grew AND the tail message changed.
  // Length-only would fire on prepend (infinite scroll loading older history);
  // tail-only would fire on the optimistic temp-id → real-id swap.
  const prevCountRef = React.useRef(messages.length);
  const prevNewestIdRef = React.useRef(newestStreamId);
  React.useEffect(() => {
    const appended =
      messages.length > prevCountRef.current && newestStreamId !== prevNewestIdRef.current;
    prevCountRef.current = messages.length;
    prevNewestIdRef.current = newestStreamId;
    if (autoCollapsePrompts && appended) {
      setPromptOverrides(prev => (prev.size === 0 ? prev : new Map()));
    }
  }, [messages.length, newestStreamId, autoCollapsePrompts]);

  const getPromptExpanded = (streamId: string): boolean => {
    const override = promptOverrides.get(streamId);
    if (override !== undefined) {
      return override;
    }
    return !autoCollapsePrompts;
  };

  const handlePromptExpandChange = (streamId: string, next: boolean): void => {
    setPromptOverrides(prev => {
      const map = new Map(prev);
      map.set(streamId, next);
      return map;
    });
  };

  // Hoist panther prompt extraction outside messages.map() so the regex scans run once,
  // not on every streaming tick. Both ops are no-ops until the last message is finished.
  const lastMessage = messages[messages.length - 1];
  const lastFinishedResponse = lastMessage?.finished ? lastMessage.aiResponse : null;
  const lastFinishedStreamId = lastMessage?.finished ? lastMessage.streamId : null;

  const { lastMessageActions, lastMessageAiResponseText } = React.useMemo(() => {
    if (lastFinishedResponse === null) {
      return {
        lastMessageActions: [] as RecommendedAction[],
        lastMessageAiResponseText: null as string | null,
      };
    }
    const actions = extractPantherPrompts(lastFinishedResponse);
    return {
      lastMessageActions: actions,
      lastMessageAiResponseText:
        actions.length > 0 ? stripRecommendedSection(lastFinishedResponse) : null,
    };
  }, [lastFinishedResponse]);

  // Total content length triggers scroll on typewriter effect and new messages
  const totalContentLength = messages.reduce((sum, msg) => sum + msg.aiResponse.length, 0);

  // Auto-scroll behavior: track position, force scroll, and smooth scroll during streaming
  useAutoScroll({
    containerRef: scrollContainerRef,
    enabled: hasActiveStream,
    forceScrollTrigger: forceScrollToBottom,
    scrollTrigger: totalContentLength, // Triggers on typewriter effect AND new messages
    threshold: 150,
  });

  // Sentinel at the top of the message list; triggers loadMore when scrolled into view.
  // scrollDirection:'up' reverses the direction guard for correct upward-scroll detection.
  // scrollContainer uses the actual clipping container rather than 'parent' — MessageList's
  // inner Box has no overflow clipping and 'parent' would resolve to the viewport.
  const { sentinelRef: loadMoreRef } = useInfiniteScroll<HTMLDivElement>({
    loading: isLoadingMore,
    onLoadMore: loadMore ?? noop,
    scrollDirection: 'up',
    scrollContainer: scrollContainerRef,
  });

  // Scroll position compensation: when older messages are prepended (infinite scroll up),
  // scrollHeight grows but scrollTop stays the same, causing the viewport to jump.
  // Detect a prepend by checking if the first message ID changed, then adjust scrollTop
  // by the height delta so the user's reading position is preserved.
  const prevScrollHeightRef = React.useRef(0);
  const prevFirstMessageIdRef = React.useRef<string | undefined>(undefined);
  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const currentFirstId = messages[0]?.streamId;
    if (
      prevFirstMessageIdRef.current !== undefined &&
      currentFirstId !== prevFirstMessageIdRef.current
    ) {
      const delta = container.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) {
        container.scrollTop += delta;
      }
    }
    prevFirstMessageIdRef.current = currentFirstId;
    prevScrollHeightRef.current = container.scrollHeight;
    // scrollContainerRef is intentionally omitted: it's a stable ref object (identity never changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  return (
    <Box id="conversation-messages" flex="1" width="100%" px={6} pt={4} pb={4}>
      {/* Triggers loadMore when scrolled into view */}
      {hasMore && <Box ref={loadMoreRef} height="1px" data-testid="load-more-sentinel" />}
      {/* Loading indicator shown at the top while older messages are being fetched */}
      <Collapse in={isLoadingMore} unmountOnExit timeout={400}>
        <FadeIn in={isLoadingMore} mountOnEnter duration={500} offset={100}>
          <Flex justify="center" py={4} data-testid="load-more-spinner">
            <Spinner size="small" />
          </Flex>
        </FadeIn>
      </Collapse>
      {messages.map(message => {
        // Show loading spinner until AI response content arrives
        const isWaitingForResponse = !message.finished && !message.aiResponse;
        // Only the last finished message gets the rich recommended-actions card
        const isLastFinished = message.streamId === lastFinishedStreamId;
        const recommendedActions = isLastFinished ? lastMessageActions : [];
        const showRecommendedActions = isLastFinished && recommendedActions.length > 0;
        // Show the Next Steps card when AI prompts exist OR the triage comment row is present.
        const showNextStepsCard =
          isLastFinished && (recommendedActions.length > 0 || Boolean(recommendedActionsHeader));
        const aiResponseText =
          isLastFinished && lastMessageAiResponseText !== null
            ? lastMessageAiResponseText
            : message.aiResponse;

        return (
          <Box key={message.streamId}>
            {/* User Message */}
            {message.userPrompt && (
              <Flex mb={4} justify="flex-end" direction="row" align="center">
                <Flex direction="column" width="80%" align="flex-start">
                  <Box
                    {...{ [MESSAGE_ROLE_ATTR]: MESSAGE_ROLE.USER }}
                    width="100%"
                    borderRadius="medium"
                    p={4}
                    wordBreak="break-word"
                    backgroundColor={tokens.background.neutral.raised[6]}
                    color={tokens.content.text.neutral.base}
                  >
                    <CollapsibleTextV3
                      lines={4}
                      toggleAlignment="flex-end"
                      expanded={getPromptExpanded(message.streamId)}
                      onExpandChange={next => handlePromptExpandChange(message.streamId, next)}
                    >
                      <PromptMarkdownPreviewV3 value={message.userPrompt} />
                    </CollapsibleTextV3>
                  </Box>
                  <Box mt={1} {...{ [PRINT_SUPPRESS_ATTR]: true }}>
                    <PromptActionsV3 prompt={message.userPrompt} />
                  </Box>
                </Flex>
              </Flex>
            )}

            {/* AI Response */}
            {!isWaitingForResponse && message.aiResponse && (
              <Box
                {...{ [MESSAGE_ROLE_ATTR]: MESSAGE_ROLE.ASSISTANT }}
                mb={showNextStepsCard ? 0 : 4}
                aria-live={message.finished ? undefined : 'polite'}
                aria-atomic="false"
                color={tokens.content.text.neutral[1]}
              >
                <ToolInvocationProvider
                  value={{
                    toolInvocations: message.toolInvocations ?? [],
                    streamId: message.streamId,
                    isCancelled: !message.finished && isCancelled,
                  }}
                >
                  <EnrichedAIResponseV3
                    response={aiResponseText}
                    finished={message.finished}
                    usage={message.usage ?? undefined}
                    onSearchRequest={onSearchRequest}
                    riskScoreEvidence={message.riskScoreEvidence ?? null}
                    onRiskExplain={onRiskExplain}
                    onPromptClick={onPromptClick}
                    suppressPromptBlocks={showRecommendedActions}
                  />
                </ToolInvocationProvider>
              </Box>
            )}

            {/* Next Steps card — shown when AI has prompts and/or the triage comment row is present.
                The comment row (recommendedActionsHeader) renders as the first item in the card.
                Only alert triage passes these props, so they never appear on the AI page. */}
            {showNextStepsCard && onPromptClick && (
              <RecommendedActionsV3
                actions={recommendedActions}
                onRun={onPromptClick}
                disabled={hasActiveStream}
                header={recommendedActionsHeader}
              />
            )}
            {/* Triage close action bar — sits below the card, alert context only. */}
            {isLastFinished && recommendedActionsRow}
          </Box>
        );
      })}
      <Flex
        align="center"
        data-testid="thinking-spinner"
        aria-hidden={!isThinking}
        sx={{
          opacity: isThinking ? 1 : 0,
          visibility: isThinking ? 'visible' : 'hidden',
          maxHeight: isThinking ? 40 : 0,
          overflow: 'hidden',
          transition: 'opacity 300ms ease, max-height 300ms ease, visibility 300ms ease',
        }}
      >
        <Spinner size="small" />
      </Flex>
    </Box>
  );
};

export default MessageListV3;
